/* SPEC-563 — the per-user sidebar LAYOUT: the pure model (no React, no `@/` alias), so
 * nav-layout.test.ts can run it under plain Node. Its backend twin is backend/nav_layout.py;
 * the two `render`s are held equal by nav-layout.fixtures.json (each side asserts its own
 * render against the same expected output).
 *
 * One name per thing:
 *   entry   "/path" or "/path?tab=key" — the favourites grammar (SPEC-452 / SPEC-456)
 *   folder  a user-named container {id, label, items}
 *   layout  the blob {schema, favourites, folders, hidden} (+ version from the server)
 *   preset  a saved snapshot of a layout's folders + hidden (never its favourites, D6)
 *
 * ACCESS IS NOT HERE. A layout is presentation (NK-52). `render` filters every entry through
 * the caller's allowed pages and sub-views (role ∩ view ∩ workspace) and the `locked` list
 * (the access-tag hook, D7): a stored entry is never trusted to open a page. The reverse
 * holds too — nothing allowed is lost: a page filed in no folder renders in "New".
 *
 * Every op returns a NEW layout and never adds an entry the caller did not hand it; the
 * server validates the result against the registry on save (fails closed).
 *
 * Pure modules here do not import VALUES from each other (Node's type-stripping needs a
 * `.ts` specifier the app build refuses), so the group map and the standalone paths are
 * PARAMETERS, passed from config/nav-groups.ts by the caller. nav-layout.test.ts pins the
 * values the Sidebar passes to the ones nav-groups.ts exports. */

export const LAYOUT_SCHEMA = 1;
export const MAX_NAV_FOLDERS = 20;
export const MAX_NAV_ENTRIES = 300;
export const MAX_NAV_LABEL_LEN = 40;
export const MAX_NAV_PRESETS = 10;

export const FAVOURITES_ID = 'favourites';
export const NEW_ID = 'new';
export const FAVOURITES_LABEL = 'Favourites';
export const NEW_LABEL = 'New';
/** A FILING source that is not a group: the sections strip of a page (its sub-views). Never
 *  stored, never rendered as a group. */
export const SECTIONS_ID = 'sections';

/** The favourites grammar, identical to nav-groups' parser and the backend's `\Z` regexes. */
export const ENTRY_PAGE_RE = /^\/[A-Za-z0-9_\-/]*$/;
export const ENTRY_TAB_RE = /^(\/[A-Za-z0-9_\-/]*)\?tab=([A-Za-z0-9_-]+)$/;
export const FOLDER_ID_RE = /^f_[a-z0-9]{8}$/;

export interface NavFolder {
  id: string;
  label: string;
  items: string[];
}

export interface NavLayout {
  schema: number;
  favourites: string[];
  folders: NavFolder[];
  hidden: string[];
  /** Present on a layout the server returned; never sent back (the PUT carries base_version). */
  version?: number;
}

export interface RenderGroup {
  id: string;
  label: string;
  items: string[];
}

export interface RenderContext {
  /** The login's pages (role ∩ view ∩ workspace), in REGISTRY order — New keeps this order. */
  allowedPages: readonly string[];
  /** Sub-view keys this login may see, per page that has sub-views. Absent = none. */
  allowedTabs: Readonly<Record<string, readonly string[] | undefined>>;
  /** Pages the access-tag hook withholds from this role ([] in v1). */
  locked: readonly string[];
  /** Pages that are never a list row (Overview, Alerts — SPEC-509): never put in New. */
  standalone: readonly string[];
}

export interface EntryTarget {
  path: string;
  tabKey?: string;
}

/** The page (and sub-view) an entry names; null if it is neither accepted shape. */
export function splitEntry(entry: unknown): EntryTarget | null {
  if (typeof entry !== 'string' || entry.length > 200) return null;
  if (entry.includes('?')) {
    const m = ENTRY_TAB_RE.exec(entry);
    return m ? { path: m[1], tabKey: m[2] } : null;
  }
  return ENTRY_PAGE_RE.test(entry) ? { path: entry } : null;
}

function dedupe(entries: readonly string[]): string[] {
  const out: string[] = [];
  for (const e of entries) if (!out.includes(e)) out.push(e);
  return out;
}

/** A trimmed folder or preset name, or null when it would be refused (empty, too long, or
 *  holding a control character). The same rule as backend nav_layout.clean_label. */
export function cleanLabel(label: string): string | null {
  const text = label.trim();
  // Counted in code points, as Python's len() counts them.
  if (!text || [...text].length > MAX_NAV_LABEL_LEN) return null;
  // Controls (Cc), lone surrogates (Cs) and bidi overrides/isolates — exactly the backend's
  // nav_layout._refused_char. NOT all of category C: the server's Unicode tables are older,
  // so a new emoji would be "unassigned" there, and zero-width joiners build ordinary emoji.
  // The label cases in nav-layout.fixtures.json pin both sides to the same answers.
  if (/[\p{Cc}\p{Cs}‪-‮⁦-⁩]/u.test(text)) return null;
  return text;
}

/** A fresh client-made folder id: "f_" + 8 of [a-z0-9], unique in the layout. */
export function makeFolderId(layout: NavLayout, random: () => number = Math.random): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  // Bounded: 36^8 ids against at most 20 folders, so a clash twice running is already a
  // broken random source — fall back to a deterministic free id rather than spin.
  for (let attempt = 0; attempt < 20; attempt += 1) {
    let id = 'f_';
    for (let i = 0; i < 8; i += 1) id += alphabet[Math.floor(random() * alphabet.length) % alphabet.length];
    if (!layout.folders.some((f) => f.id === id)) return id;
  }
  for (let n = 0; ; n += 1) {
    const id = `f_${String(n).padStart(8, '0')}`;
    if (!layout.folders.some((f) => f.id === id)) return id;
  }
}

/** The id a standard (verb) folder carries: stable, so collapse state and the group mark
 *  follow it. "Plan" → "f_plan0000". */
export function standardFolderId(label: string): string {
  return `f_${(label.toLowerCase().replace(/[^a-z0-9]/g, '') + '00000000').slice(0, 8)}`;
}

/** The standard menu as a layout: one folder per verb group, in order, plus the favourites.
 *  This is what a login with no stored layout sees, and what Reset writes. */
export function standardLayout(
  groups: ReadonlyArray<{ label: string; paths: readonly string[] }>,
  favourites: readonly string[] = [],
): NavLayout {
  return {
    schema: LAYOUT_SCHEMA,
    favourites: dedupe(favourites),
    folders: groups.map((g) => ({ id: standardFolderId(g.label), label: g.label, items: [...g.paths] })),
    hidden: [],
  };
}

/** Make an unknown blob safe to hold: malformed parts dropped, never thrown. */
export function normalise(raw: unknown): NavLayout | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const entries = (v: unknown) =>
    Array.isArray(v) ? dedupe(v.filter((e): e is string => splitEntry(e) !== null)) : [];
  const folders: NavFolder[] = [];
  const filed = new Set<string>();
  for (const f of Array.isArray(r.folders) ? r.folders : []) {
    if (!f || typeof f !== 'object') continue;
    const { id, label, items } = f as Record<string, unknown>;
    if (typeof id !== 'string' || !FOLDER_ID_RE.test(id) || folders.some((x) => x.id === id)) continue;
    const clean = typeof label === 'string' ? cleanLabel(label) : null;
    if (clean === null) continue;
    const kept = entries(items).filter((e) => !filed.has(e));
    kept.forEach((e) => filed.add(e));
    folders.push({ id, label: clean, items: kept });
  }
  const out: NavLayout = {
    schema: LAYOUT_SCHEMA,
    favourites: entries(r.favourites),
    folders,
    hidden: entries(r.hidden),
  };
  if (typeof r.version === 'number') out.version = r.version;
  return out;
}

/** The groups the sidebar shows, in order: Favourites, New, then each folder.
 *  Favourites and New are omitted when empty. A folder whose every stored item is filtered
 *  away is omitted; a folder with NO stored items stays (the user just made it). */
export function render(layout: NavLayout, ctx: RenderContext): RenderGroup[] {
  const allowed = new Set(ctx.allowedPages);
  const locked = new Set(ctx.locked);
  const hidden = new Set(layout.hidden);
  const visible = (entry: string): boolean => {
    const t = splitEntry(entry);
    if (!t || !allowed.has(t.path) || locked.has(t.path) || hidden.has(entry)) return false;
    return t.tabKey === undefined || (ctx.allowedTabs[t.path] ?? []).includes(t.tabKey);
  };

  const out: RenderGroup[] = [];
  const favourites = dedupe(layout.favourites.filter(visible));
  if (favourites.length) out.push({ id: FAVOURITES_ID, label: FAVOURITES_LABEL, items: favourites });

  const filed = new Set(layout.folders.flatMap((f) => f.items));
  const fresh = dedupe(ctx.allowedPages).filter(
    (p) => !filed.has(p) && !ctx.standalone.includes(p) && visible(p),
  );
  if (fresh.length) out.push({ id: NEW_ID, label: NEW_LABEL, items: fresh });

  for (const f of layout.folders) {
    const items = f.items.filter(visible);
    if (f.items.length && !items.length) continue;
    out.push({ id: f.id, label: f.label, items });
  }
  return out;
}

/* ── Ops. Each returns a new layout; an op that cannot apply returns the input unchanged. ── */

function clone(layout: NavLayout): NavLayout {
  return {
    schema: LAYOUT_SCHEMA,
    favourites: [...layout.favourites],
    folders: layout.folders.map((f) => ({ ...f, items: [...f.items] })),
    hidden: [...layout.hidden],
  };
}

function clamp(index: number, length: number): number {
  return Math.max(0, Math.min(Number.isFinite(index) ? Math.trunc(index) : length, length));
}

/** Move an entry to `index` in a folder (cut, never copy: an entry sits in one folder).
 *  `toId` NEW_ID unfiles it (it renders in New); FAVOURITES_ID reorders the favourites and
 *  leaves the folders alone (a starred entry may also sit in a folder). */
export function moveEntry(layout: NavLayout, entry: string, toId: string, index: number): NavLayout {
  const next = clone(layout);
  if (toId === FAVOURITES_ID) {
    if (!next.favourites.includes(entry)) return layout;
    const rest = next.favourites.filter((e) => e !== entry);
    rest.splice(clamp(index, rest.length), 0, entry);
    next.favourites = rest;
    return next;
  }
  const target = next.folders.find((f) => f.id === toId);
  if (toId !== NEW_ID && !target) return layout;
  for (const f of next.folders) f.items = f.items.filter((e) => e !== entry);
  if (target) target.items.splice(clamp(index, target.items.length), 0, entry);
  return next;
}

export type Nudge = 'up' | 'down' | 'top' | 'bottom';

/** Move an entry one step (or to an end) among the rows the user can SEE in that group.
 *  The stored list may hold entries this login cannot see; a step skips past them, so
 *  "Move up" always swaps with the row shown above. */
export function nudgeEntry(
  layout: NavLayout, groupId: string, entry: string, visible: readonly string[], to: Nudge,
): NavLayout {
  const i = visible.indexOf(entry);
  if (i < 0) return layout;
  const stored = groupId === FAVOURITES_ID
    ? layout.favourites
    : layout.folders.find((f) => f.id === groupId)?.items;
  if (!stored) return layout;
  const last = visible.length - 1;
  if ((to === 'up' || to === 'top') && i === 0) return layout;
  if ((to === 'down' || to === 'bottom') && i === last) return layout;
  const without = stored.filter((e) => e !== entry);
  const anchor = { up: visible[i - 1], top: visible[0], down: visible[i + 1], bottom: visible[last] }[to];
  const at = without.indexOf(anchor);
  if (at < 0) return layout;
  const index = to === 'up' || to === 'top' ? at : at + 1;
  return moveEntry(layout, entry, groupId, index);
}

/** Move a folder to `index` among the folders. */
export function moveFolder(layout: NavLayout, folderId: string, index: number): NavLayout {
  const from = layout.folders.findIndex((f) => f.id === folderId);
  if (from < 0) return layout;
  const next = clone(layout);
  const [folder] = next.folders.splice(from, 1);
  next.folders.splice(clamp(index, next.folders.length), 0, folder);
  return next;
}

/** Hide an entry. Its folder slot stays, so Show puts it back where it was. */
export function hide(layout: NavLayout, entry: string): NavLayout {
  if (layout.hidden.includes(entry)) return layout;
  const next = clone(layout);
  next.hidden.push(entry);
  return next;
}

/** Show a hidden entry again, in the slot it kept. */
export function show(layout: NavLayout, entry: string): NavLayout {
  if (!layout.hidden.includes(entry)) return layout;
  const next = clone(layout);
  next.hidden = next.hidden.filter((e) => e !== entry);
  return next;
}

/** Rename a folder. An empty, over-long or control-character name leaves it unchanged. */
export function renameFolder(layout: NavLayout, folderId: string, label: string): NavLayout {
  const clean = cleanLabel(label);
  const folder = layout.folders.find((f) => f.id === folderId);
  if (clean === null || !folder || folder.label === clean) return layout;
  const next = clone(layout);
  next.folders.find((f) => f.id === folderId)!.label = clean;
  return next;
}

/** Add an empty folder at the top of the folders. Refused past MAX_NAV_FOLDERS or for a
 *  name `cleanLabel` refuses. `id` must be fresh (makeFolderId). */
export function newFolder(layout: NavLayout, label: string, id: string): NavLayout {
  const clean = cleanLabel(label);
  if (clean === null || layout.folders.length >= MAX_NAV_FOLDERS) return layout;
  if (!FOLDER_ID_RE.test(id) || layout.folders.some((f) => f.id === id)) return layout;
  const next = clone(layout);
  next.folders.unshift({ id, label: clean, items: [] });
  return next;
}

/** Delete a folder. Its screens are NOT hidden: they leave the layout and render in New (D5). */
export function deleteFolder(layout: NavLayout, folderId: string): NavLayout {
  if (!layout.folders.some((f) => f.id === folderId)) return layout;
  const next = clone(layout);
  next.folders = next.folders.filter((f) => f.id !== folderId);
  return next;
}

/** The star. Adds the entry to the end of Favourites (and shows it, if it was hidden — a
 *  star on a screen you then cannot see would be a lie), or removes it. */
export function toggleFavourite(layout: NavLayout, entry: string): NavLayout {
  const next = clone(layout);
  if (next.favourites.includes(entry)) {
    next.favourites = next.favourites.filter((e) => e !== entry);
  } else {
    next.favourites.push(entry);
    next.hidden = next.hidden.filter((e) => e !== entry);
  }
  return next;
}

/** File every entry currently in New into one folder, at its end. */
export function fileNewPages(layout: NavLayout, newItems: readonly string[], folderId: string): NavLayout {
  return newItems.reduce((acc, entry) => {
    const folder = acc.folders.find((f) => f.id === folderId);
    return folder ? moveEntry(acc, entry, folderId, folder.items.length) : acc;
  }, layout);
}

/** Apply a preset: its folders + hidden, the CURRENT favourites (D6). */
export function applyPreset(
  layout: NavLayout, preset: { folders: NavFolder[]; hidden: string[] },
): NavLayout {
  const base = normalise({ schema: LAYOUT_SCHEMA, favourites: layout.favourites, folders: preset.folders, hidden: preset.hidden });
  return base ?? layout;
}

/** The body a layout is saved with (the server owns `version`). */
export function toWire(layout: NavLayout): Omit<NavLayout, 'version'> {
  return { schema: LAYOUT_SCHEMA, favourites: layout.favourites, folders: layout.folders, hidden: layout.hidden };
}

/** True when the layout is the standard menu for these groups (no customisation). */
export function isStandard(
  layout: NavLayout, groups: ReadonlyArray<{ label: string; paths: readonly string[] }>,
): boolean {
  const std = standardLayout(groups, layout.favourites);
  return JSON.stringify(toWire(std)) === JSON.stringify(toWire(layout));
}

/* ── SPEC-563 fix loop 2 (Codex promotion read, 15 Oct) ─────────────────────────────── */

export type LayoutOp = (layout: NavLayout) => NavLayout;

/** Star or un-star an entry, idempotently: the result is the same however often it runs. */
export function setFavourite(layout: NavLayout, entry: string, on: boolean): NavLayout {
  if (layout.favourites.includes(entry) === on) {
    // Already in the wanted state. Starring still shows a hidden screen (toggleFavourite's rule).
    return on && layout.hidden.includes(entry) ? show(layout, entry) : layout;
  }
  return toggleFavourite(layout, entry);
}

/** The star's op. The INTENT ("star it" / "un-star it") is decided from the layout ON SCREEN
 *  when it was pressed, and the op only ensures that state. A save that 409s replays its ops
 *  on the server's newer layout; a toggle replayed there would undo the same star made in the
 *  other tab (Codex finding 2). */
export function favouriteOp(shown: NavLayout, entry: string): LayoutOp {
  const on = !shown.favourites.includes(entry);
  return (l) => setFavourite(l, entry, on);
}

/** Where "Move to" / "File in" can send an entry from `groupId`.
 *  A folder row: every other folder, plus New for a whole page (a sub-view is never "New").
 *  A FILING source — Favourites (Codex finding 4) or a page's own sections (SECTIONS_ID, Param
 *  15 Oct: "move the nested pages… into a new folder"): every folder that does not already
 *  hold the entry. Filing leaves the source as it was: a starred entry stays starred (D3), and
 *  a section stays in its page's section list; the folder gains its own row for it, which opens
 *  the page on that section. One mechanism for both. */
export function moveDestinations(
  layout: NavLayout, groupId: string, entry: string, folders: ReadonlyArray<{ id: string; label: string }>,
): Array<{ id: string; label: string }> {
  if (groupId === FAVOURITES_ID || groupId === SECTIONS_ID) {
    const holder = layout.folders.find((f) => f.items.includes(entry))?.id;
    return folders.filter((f) => f.id !== holder).map((f) => ({ id: f.id, label: f.label }));
  }
  const isTab = splitEntry(entry)?.tabKey !== undefined;
  return [
    ...folders.filter((f) => f.id !== groupId).map((f) => ({ id: f.id, label: f.label })),
    ...(groupId !== NEW_ID && !isTab ? [{ id: NEW_ID, label: 'New (unfiled)' }] : []),
  ];
}

export interface SavedState { layout: NavLayout; version: number }
export type SaveOutcome<S extends SavedState> =
  | { kind: 'saved'; state: S }
  | { kind: 'no-longer-applies'; state: S }
  | { kind: 'behind'; state: S }
  | { kind: 'failed'; error: unknown; state?: S }
  | { kind: 'abandoned' };

/** How many times a save re-applies its ops after a 409 before it gives up. */
export const MAX_RECONCILES = 2;

/** Save `ops` on top of `base`, reconciling each 409 against the server's newer layout.
 *
 *  Every 409 carries the server's CURRENT layout and version; the next attempt is built on
 *  THAT one — the latest, never an earlier one (Codex finding 3: keeping the first 409's
 *  version hid the second's changes and left the next save on a stale base). At most
 *  MAX_RECONCILES re-tries. Outcomes, each carrying the newest server state seen:
 *    saved              the ops are stored
 *    no-longer-applies  on the newest layout the ops change nothing; nothing more is sent
 *    behind             still conflicting after the bound; the caller shows the newest layout
 *    failed             a non-409 error; `state` is set only if a 409 was seen first
 *    abandoned          `proceed()` turned false between attempts (the login changed): no
 *                       further PUT is sent, and the caller must not touch its state */
export async function saveWithReconcile<S extends SavedState>(args: {
  base: S;
  ops: readonly LayoutOp[];
  put: (version: number, layout: NavLayout) => Promise<S>;
  conflictOf: (err: unknown) => S | null;
  maxReconciles?: number;
  /** Checked before every retry. False = stop: a retry after a login change would send one
   *  login's layout and version under another login's token (fix loop 2 review). */
  proceed?: () => boolean;
}): Promise<SaveOutcome<S>> {
  const max = args.maxReconciles ?? MAX_RECONCILES;
  const build = (s: S) => args.ops.reduce((l, op) => op(l), s.layout);
  let state = args.base;
  let seenConflict = false;
  for (let reconciles = 0; ; reconciles += 1) {
    const next = build(state);
    if (seenConflict && JSON.stringify(toWire(next)) === JSON.stringify(toWire(state.layout))) {
      return { kind: 'no-longer-applies', state };
    }
    try {
      return { kind: 'saved', state: await args.put(state.version, next) };
    } catch (err) {
      const server = args.conflictOf(err);
      if (!server) return seenConflict ? { kind: 'failed', error: err, state } : { kind: 'failed', error: err };
      state = server;
      seenConflict = true;
      if (args.proceed && !args.proceed()) return { kind: 'abandoned' };
      if (reconciles >= max) return { kind: 'behind', state };
    }
  }
}

/* ── Item 6 review (claude-sonnet-5-5, fix loop 2) ──────────────────────────────────── */

/** File an entry into a folder: cut from any other folder (one folder per entry) and SHOWN.
 *  Filing is an explicit "put it here", so a hidden entry becomes visible — otherwise a filed
 *  hidden section rendered nowhere while the menu said it was filed (review M1). A folder that
 *  no longer exists (deleted in another window) leaves the layout unchanged. Used by both
 *  filing paths: Favourites "File in" and a page's "File a section". */
export function fileEntry(layout: NavLayout, entry: string, folderId: string): NavLayout {
  const folder = layout.folders.find((f) => f.id === folderId);
  return folder ? show(moveEntry(layout, entry, folderId, folder.items.length), entry) : layout;
}

/** The sections a page row offers to file: the page's REGISTRY tabs (its sub-groups, in order)
 *  that this login may see (`allowedKeys` = Sidebar.allowedSubKeys, the same list render() is
 *  given), as `/path?tab=key` entries. Nothing else is ever offered (review L6). */
export function offeredSections(
  path: string,
  groups: ReadonlyArray<{ items: ReadonlyArray<{ key: string; label: string }> }> | undefined,
  allowedKeys: readonly string[] | undefined,
): Array<{ entry: string; label: string }> {
  if (!groups || !allowedKeys) return [];
  return groups
    .flatMap((g) => g.items)
    .filter((t) => allowedKeys.includes(t.key))
    .map((t) => ({ entry: `${path}?tab=${t.key}`, label: t.label }));
}

/** The group the menu follows to for the page on screen (SPEC-505), never Favourites. A
 *  section row filed in a folder wins when it is the section on screen, so clicking it does not
 *  pull the menu to the page's own group (review L3). Otherwise the group holding the page row. */
export function followTarget(
  groups: ReadonlyArray<{ id: string; items: readonly string[] }>,
  isActivePath: (path: string) => boolean,
  activeTab: string,
): string | null {
  const folders = groups.filter((g) => g.id !== FAVOURITES_ID);
  if (activeTab) {
    const hit = folders.find((g) => g.items.some((e) => {
      const t = splitEntry(e);
      return Boolean(t?.tabKey) && t!.tabKey === activeTab && isActivePath(t!.path);
    }));
    if (hit) return hit.id;
  }
  return folders.find((g) => g.items.some((e) => !e.includes('?') && isActivePath(e)))?.id ?? null;
}

/* ── SPEC-563 M2c — drag (D2, Param 15 Oct: native HTML5 drag, no dependency) ─────────── */

/** Where a drop lands relative to the row or heading under the pointer. */
export type DropZone = 'before' | 'after';

/** The hit zone: the upper half of a target is "before" it, the lower half (from the
 *  midline down) "after" it. A folder heading takes a dragged ROW "into" the folder whatever
 *  the zone; the zone matters on a heading only when a FOLDER is dragged. */
export function dropZone(offsetY: number, height: number): DropZone {
  return height > 0 && offsetY < height / 2 ? 'before' : 'after';
}

/** What is being dragged: a row (its group and stored entry) or a stored folder. */
export type DragItem =
  | { kind: 'entry'; group: string; entry: string }
  | { kind: 'folder'; folder: string };

/** What it is dropped on: a row of a group, or a group's heading. */
export type DropTarget =
  | { kind: 'row'; group: string; entry: string; zone: DropZone }
  | { kind: 'heading'; group: string; zone: DropZone };

/** Why a drop is refused. Each is a move the "…" menu does not offer either. */
export type DropRefusal =
  | 'no-change'           // the menu would look the same
  | 'favourites-by-star'  // only the star adds to Favourites
  | 'not-to-new'          // a section, or a Favourites row, never goes to New
  | 'new-has-no-order'    // New keeps the standard order
  | 'already-there'       // a Favourites row onto the folder that already holds it
  | 'folders-only'        // a folder goes only onto another folder's heading
  | 'folder-gone'         // the target folder no longer exists
  | 'not-movable'         // the dragged row is no longer in the layout
  | 'anchor-gone';        // the row it was dropped next to is no longer in the layout

export type DropPlan = { ok: true; op: LayoutOp } | { ok: false; reason: DropRefusal };

/** The index to insert `moving` at in `stored` (cut first), next to the anchor row: right
 *  before it, or right after it. Anchor-based, as nudgeEntry is, so a stored entry this login
 *  cannot see between two rows never shifts the result, and the subtract-one for a row
 *  dragged down its own list falls out of the cut. -1 when the anchor is not there.
 *  Replay limit (accepted, review L11): `drag.group` is the group at drag time; on a 409
 *  replay the legality checks use it as given, and the result is still a valid layout. */
function anchorIndex(stored: readonly string[], moving: string, target: DropTarget): number {
  if (target.kind === 'heading') return Number.MAX_SAFE_INTEGER;
  const at = stored.filter((e) => e !== moving).indexOf(target.entry);
  return at < 0 ? -1 : target.zone === 'before' ? at : at + 1;
}

function dropEntry(
  layout: NavLayout, drag: Extract<DragItem, { kind: 'entry' }>, target: DropTarget,
): NavLayout | DropRefusal {
  const { entry, group: from } = drag;
  const to = target.group;
  if (target.kind === 'row' && target.entry === entry) return 'no-change';
  if (to === FAVOURITES_ID) {
    if (from !== FAVOURITES_ID) return 'favourites-by-star';
    if (!layout.favourites.includes(entry)) return 'not-movable';
    const index = anchorIndex(layout.favourites, entry, target);
    return index < 0 ? 'anchor-gone' : moveEntry(layout, entry, FAVOURITES_ID, index);
  }
  if (to === NEW_ID) {
    if (from === NEW_ID) return 'new-has-no-order';
    // The menu's own rule: New is a destination for a whole page from a folder only.
    if (!moveDestinations(layout, from, entry, layout.folders).some((d) => d.id === NEW_ID)) return 'not-to-new';
    return moveEntry(layout, entry, NEW_ID, 0);
  }
  const folder = layout.folders.find((f) => f.id === to);
  if (!folder) return 'folder-gone';
  if (from === FAVOURITES_ID) {
    if (!layout.favourites.includes(entry)) return 'not-movable';
    // "File in" (moveDestinations): never the folder that already holds it.
    if (!moveDestinations(layout, FAVOURITES_ID, entry, layout.folders).some((d) => d.id === to)) return 'already-there';
    const index = anchorIndex(folder.items, entry, target);
    // fileEntry's rule, at a place: cut from any other folder, then shown.
    return index < 0 ? 'anchor-gone' : show(moveEntry(layout, entry, to, index), entry);
  }
  const index = anchorIndex(folder.items, entry, target);
  return index < 0 ? 'anchor-gone' : moveEntry(layout, entry, to, index);
}

function dropFolder(
  layout: NavLayout, drag: Extract<DragItem, { kind: 'folder' }>, target: DropTarget,
): NavLayout | DropRefusal {
  if (!layout.folders.some((f) => f.id === drag.folder)) return 'not-movable';
  if (target.kind !== 'heading' || target.group === FAVOURITES_ID || target.group === NEW_ID) return 'folders-only';
  if (target.group === drag.folder) return 'no-change';
  // Position among the STORED folders, as the menu's Move folder up/down counts them.
  const at = layout.folders.map((f) => f.id).filter((id) => id !== drag.folder).indexOf(target.group);
  if (at < 0) return 'folder-gone';
  return moveFolder(layout, drag.folder, target.zone === 'before' ? at : at + 1);
}

function resolveDrop(layout: NavLayout, drag: DragItem, target: DropTarget): NavLayout | DropRefusal {
  return drag.kind === 'entry' ? dropEntry(layout, drag, target) : dropFolder(layout, drag, target);
}

/** Plan a drop onto the existing ops. Drag adds no rule: every move it makes is one the "…"
 *  menu also makes (moveEntry, moveFolder, fileEntry's cut-and-show), and every move the menu
 *  does not offer is refused with a reason the caller announces. A drop that would leave the
 *  layout as it is is refused as 'no-change' (no save, no "moved" message).
 *  The returned op re-plans on whatever layout it is applied to, so a save that 409s replays
 *  the drop by its anchor row on the newer layout; where the drop no longer applies there it
 *  returns that layout unchanged. */
export function planDrop(layout: NavLayout, drag: DragItem, target: DropTarget): DropPlan {
  const next = resolveDrop(layout, drag, target);
  if (typeof next === 'string') return { ok: false, reason: next };
  if (JSON.stringify(toWire(next)) === JSON.stringify(toWire(layout))) return { ok: false, reason: 'no-change' };
  return {
    ok: true,
    op: (l) => {
      const replayed = resolveDrop(l, drag, target);
      return typeof replayed === 'string' ? l : replayed;
    },
  };
}
